import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Book, 
  ChevronLeft, 
  ChevronRight,
  Download,
  ShoppingCart,
  Loader2,
  Image as ImageIcon,
  Quote
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

const COLOR_SCHEMES = {
  classic: { primary: '#161845', accent: '#690FC9', bg: '#F9FAFB', text: '#1F2937' },
  warm: { primary: '#5D4037', accent: '#8D6E63', bg: '#FFF8E1', text: '#3E2723' },
  navy: { primary: '#1A237E', accent: '#FFD700', bg: '#E8EAF6', text: '#1A237E' },
  forest: { primary: '#1B5E20', accent: '#4CAF50', bg: '#E8F5E9', text: '#1B5E20' },
  burgundy: { primary: '#6D1F3A', accent: '#C2185B', bg: '#FCE4EC', text: '#6D1F3A' }
};

// Watermark component - renders multiple watermarks across the page
function PreviewWatermark() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-50">
      {/* Diagonal watermarks pattern */}
      <div className="absolute inset-0 flex flex-col justify-around">
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex justify-around">
            {[0, 1].map((col) => (
              <span 
                key={col}
                className="text-black/10 text-4xl font-bold rotate-[-30deg] select-none whitespace-nowrap"
                style={{ textShadow: '0 0 1px rgba(0,0,0,0.1)' }}
              >
                PREVIEW ONLY
              </span>
            ))}
          </div>
        ))}
      </div>
      {/* Center watermark - larger */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span 
          className="text-black/15 text-6xl font-bold rotate-[-30deg] select-none whitespace-nowrap"
          style={{ textShadow: '0 0 2px rgba(0,0,0,0.1)' }}
        >
          PREVIEW
        </span>
      </div>
      {/* Bottom watermark strip */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/5 py-1 text-center">
        <span className="text-black/30 text-xs font-medium">
          Preview Only - Order to receive printed book without watermarks
        </span>
      </div>
    </div>
  );
}

export default function BookPreviewPage() {
  const { token } = useParams();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  
  // Load preview data
  useEffect(() => {
    const loadPreview = async () => {
      try {
        const previewDoc = await getDoc(doc(db, 'bookPreviews', token));
        if (previewDoc.exists()) {
          setPreview(previewDoc.data());
        } else {
          setError('Preview not found or expired');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error loading preview:', err);
        setError('Failed to load preview');
        setLoading(false);
      }
    };
    loadPreview();
  }, [token]);
  
  // Get colors
  const colors = preview ? (COLOR_SCHEMES[preview.colorScheme] || COLOR_SCHEMES.classic) : COLOR_SCHEMES.classic;
  
  // Check if we have narrative content
  const hasNarrative = preview?.narrative?.chapters?.length > 0;
  const narrative = preview?.narrative || {};
  const photos = preview?.photos || [];
  
  // Build pages array based on whether we have narrative or raw transcripts
  const pages = [];
  
  if (preview) {
    // Cover page
    pages.push({ type: 'cover' });
    
    // Title page
    pages.push({ type: 'title' });
    
    // Dedication
    if (preview.dedication || narrative.dedication) {
      pages.push({ type: 'dedication' });
    }
    
    // About / Introduction
    if (narrative.aboutText || preview.summaries?.about) {
      pages.push({ type: 'about' });
    }
    
    if (hasNarrative) {
      // Narrative mode: chapters with pull quotes and photos
      let photoIndex = 0;
      
      narrative.chapters?.forEach((chapter, chapterIndex) => {
        // Chapter title page
        pages.push({ type: 'chapterTitle', data: chapter, index: chapterIndex });
        
        // Split chapter content into pages (roughly 300 words per page)
        const words = chapter.content?.split(' ') || [];
        const wordsPerPage = 250;
        
        for (let i = 0; i < words.length; i += wordsPerPage) {
          const pageWords = words.slice(i, i + wordsPerPage);
          const isFirstPage = i === 0;
          const isLastPage = i + wordsPerPage >= words.length;
          
          // Get a pull quote for this page section
          const pullQuoteIndex = Math.floor((i / wordsPerPage) % (chapter.pullQuotes?.length || 1));
          const pullQuote = chapter.pullQuotes?.[pullQuoteIndex];
          
          pages.push({
            type: 'chapterContent',
            data: {
              content: pageWords.join(' '),
              chapterTitle: isFirstPage ? chapter.title : null,
              pullQuote: isFirstPage && pullQuote ? pullQuote : null
            }
          });
        }
        
        // Add suggested photos for this chapter
        if (chapter.suggestedPhotos?.length > 0) {
          chapter.suggestedPhotos.forEach(photoNum => {
            const photo = photos[photoNum - 1];
            if (photo && photoIndex < photos.length) {
              pages.push({ type: 'photo', data: photo });
              photoIndex++;
            }
          });
        }
      });
      
      // Add any remaining photos
      while (photoIndex < photos.length) {
        pages.push({ type: 'photo', data: photos[photoIndex] });
        photoIndex++;
      }
      
    } else {
      // Fallback: raw transcript mode
      const responses = (preview.responses || []).filter(r => {
        if (r.section === 'consent') return false;
        if (r.questionId?.startsWith('consent_') || r.questionId?.startsWith('intro_')) return false;
        return true;
      });
      
      let photoIndex = 0;
      responses.forEach((response, i) => {
        pages.push({ type: 'story', data: response });
        
        // Add photos periodically
        if (photoIndex < photos.length && (i + 1) % 3 === 0) {
          pages.push({ type: 'photo', data: photos[photoIndex] });
          photoIndex++;
        }
      });
      
      // Add remaining photos
      while (photoIndex < photos.length) {
        pages.push({ type: 'photo', data: photos[photoIndex] });
        photoIndex++;
      }
    }
    
    // Back cover
    pages.push({ type: 'backCover' });
  }
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' && currentPage < pages.length - 1) {
        setCurrentPage(prev => prev + 1);
      } else if (e.key === 'ArrowLeft' && currentPage > 0) {
        setCurrentPage(prev => prev - 1);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, pages.length]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto" />
          <p className="mt-4 text-gray-300">Loading preview...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Book className="w-16 h-16 text-gray-600 mx-auto" />
          <p className="mt-4 text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  const currentPageData = pages[currentPage];
  
  // Render page content based on type
  const renderPage = () => {
    switch (currentPageData?.type) {
      case 'cover':
        return (
          <div 
            className="w-full h-full flex flex-col relative overflow-hidden"
            style={{ backgroundColor: colors.primary }}
          >
            {preview.coverPhotoUrl && (
              <div className="absolute inset-0">
                <img 
                  src={preview.coverPhotoUrl} 
                  alt="Cover" 
                  className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              </div>
            )}
            <div className="relative z-10 flex-1 flex flex-col justify-end p-8 pb-12">
              <h1 
                className="text-4xl font-serif font-bold mb-4 text-white"
                style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}
              >
                {narrative.title || `${preview.residentName}'s Life Story`}
              </h1>
              {narrative.subtitle && (
                <p className="text-xl text-gray-200 italic">{narrative.subtitle}</p>
              )}
            </div>
          </div>
        );
        
      case 'title':
        return (
          <div 
            className="w-full h-full flex flex-col items-center justify-center p-8 text-center"
            style={{ backgroundColor: colors.bg }}
          >
            <div className="w-24 h-1 mb-8" style={{ backgroundColor: colors.accent }} />
            <h1 
              className="text-3xl font-serif font-bold mb-4"
              style={{ color: colors.primary }}
            >
              {narrative.title || `${preview.residentName}'s Life Story`}
            </h1>
            {narrative.subtitle && (
              <p className="text-lg italic mb-8" style={{ color: colors.text }}>
                {narrative.subtitle}
              </p>
            )}
            <p className="text-sm" style={{ color: colors.text }}>
              A collection of memories and stories
            </p>
            <div className="w-24 h-1 mt-8" style={{ backgroundColor: colors.accent }} />
          </div>
        );
        
      case 'dedication':
        return (
          <div 
            className="w-full h-full flex flex-col items-center justify-center p-12 text-center"
            style={{ backgroundColor: colors.bg }}
          >
            <Quote className="w-8 h-8 mb-6" style={{ color: colors.accent }} />
            <p 
              className="text-lg italic max-w-md leading-relaxed"
              style={{ color: colors.text }}
            >
              {preview.dedication || narrative.dedication}
            </p>
          </div>
        );
        
      case 'about':
        return (
          <div 
            className="w-full h-full flex flex-col p-8"
            style={{ backgroundColor: colors.bg }}
          >
            <h2 
              className="text-2xl font-serif font-bold mb-6"
              style={{ color: colors.primary }}
            >
              About {preview.residentName}
            </h2>
            <p 
              className="text-base leading-relaxed"
              style={{ color: colors.text }}
            >
              {narrative.aboutText || preview.summaries?.about}
            </p>
          </div>
        );
        
      case 'chapterTitle':
        return (
          <div 
            className="w-full h-full flex flex-col items-center justify-center p-8 text-center"
            style={{ backgroundColor: colors.primary }}
          >
            <span className="text-sm uppercase tracking-widest text-white/70 mb-4">
              Chapter {currentPageData.index + 1}
            </span>
            <h2 className="text-3xl font-serif font-bold text-white mb-6">
              {currentPageData.data.title}
            </h2>
            <div className="w-16 h-1" style={{ backgroundColor: colors.accent }} />
          </div>
        );
        
      case 'chapterContent':
        return (
          <div 
            className="w-full h-full flex flex-col p-8 overflow-hidden"
            style={{ backgroundColor: colors.bg }}
          >
            {currentPageData.data.chapterTitle && (
              <h3 
                className="text-xl font-serif font-bold mb-4"
                style={{ color: colors.primary }}
              >
                {currentPageData.data.chapterTitle}
              </h3>
            )}
            
            {/* Pull quote */}
            {currentPageData.data.pullQuote && (
              <div 
                className="mb-6 pl-4 py-2 border-l-4"
                style={{ borderColor: colors.accent }}
              >
                <p 
                  className="text-lg italic"
                  style={{ color: colors.primary }}
                >
                  "{currentPageData.data.pullQuote.quote}"
                </p>
                {currentPageData.data.pullQuote.context && (
                  <p className="text-sm mt-1" style={{ color: colors.text }}>
                    — {currentPageData.data.pullQuote.context}
                  </p>
                )}
              </div>
            )}
            
            <p 
              className="text-base leading-relaxed flex-1"
              style={{ color: colors.text }}
            >
              {currentPageData.data.content}
            </p>
          </div>
        );
        
      case 'story':
        // Fallback for raw transcript mode
        return (
          <div 
            className="w-full h-full flex flex-col p-8"
            style={{ backgroundColor: colors.bg }}
          >
            <p 
              className="text-sm font-medium mb-3 uppercase tracking-wide"
              style={{ color: colors.accent }}
            >
              {currentPageData.data.section}
            </p>
            <h3 
              className="text-lg font-serif italic mb-4"
              style={{ color: colors.primary }}
            >
              "{currentPageData.data.question}"
            </h3>
            <p 
              className="text-base leading-relaxed"
              style={{ color: colors.text }}
            >
              {currentPageData.data.transcript}
            </p>
          </div>
        );
        
      case 'photo':
        return (
          <div 
            className="w-full h-full flex flex-col"
            style={{ backgroundColor: colors.bg }}
          >
            <div className="flex-1 p-4">
              <img 
                src={currentPageData.data.url} 
                alt={currentPageData.data.caption || 'Photo'} 
                className="w-full h-full object-contain rounded-lg shadow-lg"
              />
            </div>
            {currentPageData.data.caption && (
              <div className="p-4 pt-0 text-center">
                <p 
                  className="text-sm italic"
                  style={{ color: colors.text }}
                >
                  {currentPageData.data.caption}
                </p>
                {currentPageData.data.contributorName && (
                  <p 
                    className="text-xs mt-1"
                    style={{ color: colors.accent }}
                  >
                    Photo by {currentPageData.data.contributorName}
                  </p>
                )}
              </div>
            )}
          </div>
        );
        
      case 'backCover':
        return (
          <div 
            className="w-full h-full flex flex-col items-center justify-center p-8 text-center"
            style={{ backgroundColor: colors.primary }}
          >
            <Book className="w-16 h-16 text-white/50 mb-6" />
            <p className="text-white/70 text-sm max-w-xs">
              {narrative.aboutText || `A treasured collection of ${preview.residentName}'s life stories and memories.`}
            </p>
            <div className="mt-8 pt-8 border-t border-white/20">
              <p className="text-white/50 text-xs">
                Created with Life Story Capture
              </p>
              <p className="text-white/30 text-xs mt-1">
                quiltt.com
              </p>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 py-3 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Book className="w-6 h-6 text-white" />
            <div>
              <h1 className="text-white font-medium">Book Preview</h1>
              <p className="text-gray-400 text-sm">
                {hasNarrative ? `${narrative.chapters?.length || 0} chapters` : 'Transcript view'}
              </p>
            </div>
          </div>
          <div className="text-gray-400 text-sm">
            Page {currentPage + 1} of {pages.length}
          </div>
        </div>
      </header>
      
      {/* Book viewer */}
      <main className="flex-1 flex items-center justify-center p-4 md:p-8">
        <div className="relative w-full max-w-2xl aspect-[3/4] shadow-2xl rounded-lg overflow-hidden">
          {renderPage()}
          <PreviewWatermark />
        </div>
      </main>
      
      {/* Navigation */}
      <footer className="bg-gray-800 border-t border-gray-700 py-4 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
            disabled={currentPage === 0}
            className="flex items-center gap-2 px-4 py-2 text-white disabled:text-gray-600 hover:bg-gray-700 rounded-lg transition-colors disabled:hover:bg-transparent"
          >
            <ChevronLeft className="w-5 h-5" />
            Previous
          </button>
          
          {/* Page dots */}
          <div className="flex gap-1 max-w-xs overflow-hidden">
            {pages.slice(Math.max(0, currentPage - 5), currentPage + 6).map((_, i) => {
              const pageIndex = Math.max(0, currentPage - 5) + i;
              return (
                <button
                  key={pageIndex}
                  onClick={() => setCurrentPage(pageIndex)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    pageIndex === currentPage ? 'bg-white' : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                />
              );
            })}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(pages.length - 1, prev + 1))}
            disabled={currentPage === pages.length - 1}
            className="flex items-center gap-2 px-4 py-2 text-white disabled:text-gray-600 hover:bg-gray-700 rounded-lg transition-colors disabled:hover:bg-transparent"
          >
            Next
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-center text-gray-500 text-xs mt-3">
          Use arrow keys to navigate • This is a preview only
        </p>
      </footer>
    </div>
  );
}
